import {
  Box,
  Button,
  Card,
  Grid,
  Heading,
  Input,
  NativeSelect,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router'

import { Field, Toggle } from '@mangovoice/mango-ui'
import { ExtensionType } from '@mangovoice/types'
import type { ApiError, ExtensionInstance } from '@mangovoice/types'

import { UserGroupPickList } from '../../components/UserGroupPickList'
import {
  useAutoSelectEveryoneGroup,
  useCreateExtension,
  useGetExtensionInfo,
  useListExtensions,
  usePbxUserGroups,
  useResendSecureFaxLink,
  useUpdateExtension,
  useUpdateExtensionUserGroups,
} from '../../hooks'
import { useExtensionsI18n } from '../../i18n/provider'
import type { VirtualFaxSeedFields } from '../../types/extension.types'
import {
  EXTENSION_TYPE_DEFAULT_NUMBER,
  getNextExtensionNumber,
  isReservedExtensionNumber,
  isValidEmailList,
  SWITCH_FORM_CSS,
  TOAST_DURATION_ERROR,
  TOAST_DURATION_SUCCESS,
} from '../../utils'
import { getListLabelForSlug, getTypeLabelForSlug } from '../typeSlugLabels'
import { renderWithWrapper } from './renderWithWrapper'
import type { ExtensionEditPageProps } from './types'

export const VirtualFaxEditPage = ({
  pbxId,
  type,
  extensionId: extensionIdParam,
  toast,
  getListRoute,
  wrapper,
  isUkEnvironment = false,
}: ExtensionEditPageProps) => {
  const { text } = useExtensionsI18n()
  const navigate = useNavigate()
  const pbxIdNum = pbxId
  const isAddMode = extensionIdParam === 'new'
  const extensionId = !isAddMode ? parseInt(extensionIdParam, 10) : undefined
  const isValidId =
    !isAddMode && extensionId != null && !Number.isNaN(extensionId)

  const {
    data: extension,
    isLoading,
    isError,
  } = useGetExtensionInfo({
    extensionId: extensionId ?? 0,
    options: { enabled: isValidId },
  })

  const { extensions: allExtensions, isLoading: isLoadingAllExtensions } =
    useListExtensions({
      pbxId: pbxIdNum,
      listParams: { limit: 1000, fetchAll: true },
      options: { enabled: isAddMode },
    })
  const { userGroups: allUserGroups } = usePbxUserGroups(pbxIdNum, '')

  const updateExtensionMutation = useUpdateExtension(pbxIdNum)
  const updateUserGroupsMutation = useUpdateExtensionUserGroups(pbxIdNum)
  const createExtensionMutation = useCreateExtension(pbxIdNum)
  const resendLinkMutation = useResendSecureFaxLink(extensionId, pbxIdNum)

  const [name, setName] = useState('')
  const [number, setNumber] = useState('')
  const [omitFromDirectory, setOmitFromDirectory] = useState(isAddMode)
  const [receivingEmailDestination, setReceivingEmailDestination] = useState('')
  const [allowMessageReview, setAllowMessageReview] = useState(false)
  const [secureFaxDownloadCount, setSecureFaxDownloadCount] = useState('5')
  const [secureFaxFailedLoginRetries, setSecureFaxFailedLoginRetries] =
    useState('5')
  const [secureFaxLinkExpiryMinutes, setSecureFaxLinkExpiryMinutes] =
    useState('2880')
  const [secureFaxPassword, setSecureFaxPassword] = useState('')
  const [secureFaxPasswordConfirmation, setSecureFaxPasswordConfirmation] =
    useState('')
  const [userGroupIds, setUserGroupIds] = useState<number[]>([])
  const [attemptedSubmit, setAttemptedSubmit] = useState(false)
  const [passwordFieldsHaveValues, setPasswordFieldsHaveValues] =
    useState(false)

  useEffect(() => {
    if (!isAddMode || isLoadingAllExtensions) return
    setNumber((prev) => {
      if (prev !== '') return prev
      const sameTypeNumbers = allExtensions
        .filter((ext) => ext.type === ExtensionType.VIRTUAL_FAX)
        .map((ext) => ext.number)
      const allNumbers = allExtensions.map((ext) => ext.number)
      return getNextExtensionNumber(
        sameTypeNumbers,
        allNumbers,
        EXTENSION_TYPE_DEFAULT_NUMBER['virtual-fax'] ?? 500,
        isUkEnvironment,
      )
    })
  }, [isAddMode, isLoadingAllExtensions, allExtensions, isUkEnvironment])

  useEffect(() => {
    if (isAddMode) {
      setName(
        (prev) =>
          prev || text('extensions.types.virtualFax', 'Virtual Fax Machine'),
      )
      return
    }
    if (extension && extension.type === ExtensionType.VIRTUAL_FAX) {
      const virtualFax = extension as ExtensionInstance & VirtualFaxSeedFields
      setName(virtualFax.name ?? '')
      setNumber(virtualFax.number ?? '')
      setOmitFromDirectory(virtualFax.omit_from_directory ?? false)
      setReceivingEmailDestination(virtualFax.receiving_email_destination ?? '')
      setAllowMessageReview(virtualFax.allow_message_review ?? true)
      setSecureFaxDownloadCount(
        virtualFax.secure_fax_download_count != null
          ? String(virtualFax.secure_fax_download_count)
          : '5',
      )
      setSecureFaxFailedLoginRetries(
        virtualFax.secure_fax_failed_login_retries != null
          ? String(virtualFax.secure_fax_failed_login_retries)
          : '5',
      )
      setSecureFaxLinkExpiryMinutes(
        virtualFax.secure_fax_link_expiry_minutes != null
          ? String(virtualFax.secure_fax_link_expiry_minutes)
          : '2880',
      )
      setUserGroupIds(virtualFax.user_groups?.map((group) => group.id) ?? [])
    }
  }, [extension, isAddMode])

  useAutoSelectEveryoneGroup(isAddMode, allUserGroups, setUserGroupIds)

  useEffect(() => {
    setPasswordFieldsHaveValues(
      secureFaxPassword.trim() !== '' ||
        secureFaxPasswordConfirmation.trim() !== '',
    )
  }, [secureFaxPassword, secureFaxPasswordConfirmation])

  const isEmailInvalid = () =>
    receivingEmailDestination.trim() !== '' &&
    !isValidEmailList(receivingEmailDestination)
  const emailInvalid = attemptedSubmit && isEmailInvalid()

  const extVf = extension as
    (ExtensionInstance & VirtualFaxSeedFields) | undefined
  const shouldShowResendButton =
    !isAddMode &&
    extVf?.resendable_secure_fax_count != null &&
    extVf.resendable_secure_fax_count > 0 &&
    !passwordFieldsHaveValues

  const listPath = getListRoute({ pbxId: pbxIdNum, type })
  const typeLabel = getTypeLabelForSlug(type, text)
  const listLabel = getListLabelForSlug(type, text)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setAttemptedSubmit(true)
    if (!name.trim() || !number.trim()) {
      toast.create({
        title: text('extensions.edit.validationError', 'Validation Error'),
        description: text(
          'extensions.edit.nameNumberRequired',
          'Name and number are required.',
        ),
        type: 'error',
        duration: TOAST_DURATION_ERROR,
        closable: true,
      })
      return
    }
    if (
      isReservedExtensionNumber(number.trim(), { isUkEnvironment }) &&
      (isAddMode || number.trim() !== (extension?.number ?? '').trim())
    ) {
      toast.create({
        title: text('extensions.edit.validationError', 'Validation Error'),
        description: text(
          'extensions.edit.reservedNumber',
          '{{number}} is reserved. Please select a different number.',
          { number: number.trim() },
        ),
        type: 'error',
        duration: TOAST_DURATION_ERROR,
        closable: true,
      })
      return
    }
    if (isEmailInvalid()) {
      toast.create({
        title: text('extensions.edit.validationError', 'Validation Error'),
        description: text(
          'extensions.edit.emailFormatInvalid',
          'Incorrect email formatting. Make sure there are no spaces. Separate multiple addresses with commas.',
        ),
        type: 'error',
        duration: TOAST_DURATION_ERROR,
        closable: true,
      })
      return
    }
    const hasPasswordMismatch =
      secureFaxPassword !== '' &&
      secureFaxPassword !== secureFaxPasswordConfirmation
    if (hasPasswordMismatch) {
      toast.create({
        title: text('extensions.edit.validationError', 'Validation Error'),
        description: text(
          'extensions.edit.virtualFax.passwordsDoNotMatch',
          'Passwords do not match.',
        ),
        type: 'error',
        duration: TOAST_DURATION_ERROR,
        closable: true,
      })
      return
    }
    if (secureFaxPassword !== '' && secureFaxPassword.length < 8) {
      toast.create({
        title: text('extensions.edit.validationError', 'Validation Error'),
        description: text(
          'extensions.edit.virtualFax.passwordMinLength',
          'Secure fax password must be at least 8 characters.',
        ),
        type: 'error',
        duration: TOAST_DURATION_ERROR,
        closable: true,
      })
      return
    }

    const patchPayload = {
      name: name.trim(),
      number: number.trim(),
      omit_from_directory: omitFromDirectory,
      receiving_email_destination: receivingEmailDestination.trim(),
      allow_message_review: allowMessageReview,
      secure_fax_download_count: parseInt(secureFaxDownloadCount, 10) || 0,
      secure_fax_failed_login_retries:
        parseInt(secureFaxFailedLoginRetries, 10) || 0,
      secure_fax_link_expiry_minutes:
        parseInt(secureFaxLinkExpiryMinutes, 10) || 0,
      ...(secureFaxPassword.trim()
        ? {
            secure_fax_password: secureFaxPassword,
            secure_fax_password_confirmation: secureFaxPasswordConfirmation,
          }
        : {}),
    }

    try {
      let targetExtensionId: number | string
      if (isAddMode) {
        const created = await createExtensionMutation.mutateAsync({
          type: ExtensionType.VIRTUAL_FAX,
          name: name.trim(),
          number: number.trim(),
          omit_from_directory: omitFromDirectory,
          receiving_email_destination: receivingEmailDestination.trim(),
        } as never)
        targetExtensionId = created.id
      } else {
        if (!extensionId) return
        targetExtensionId = extensionId
      }

      await updateExtensionMutation.mutateAsync({
        extensionId: targetExtensionId,
        payload: patchPayload,
      })
      await updateUserGroupsMutation.mutateAsync({
        extensionId: targetExtensionId,
        user_group_ids: userGroupIds,
      })
      toast.create({
        title: isAddMode
          ? text('extensions.edit.virtualFax.created', 'Virtual fax created')
          : text('extensions.edit.virtualFax.updated', 'Virtual fax updated'),
        type: 'success',
        duration: TOAST_DURATION_SUCCESS,
        closable: true,
      })
      navigate(listPath)
    } catch (error: unknown) {
      const apiErr = error as ApiError | undefined
      toast.create({
        title: isAddMode
          ? text('extensions.edit.createFailed', 'Create failed')
          : text('extensions.edit.updateFailed', 'Update failed'),
        description:
          apiErr?.message ??
          (error as Error)?.message ??
          text(
            'extensions.edit.virtualFax.saveFailed',
            'Failed to save virtual fax',
          ),
        type: 'error',
        duration: TOAST_DURATION_ERROR,
        closable: true,
      })
    }
  }

  const handleResendLink = () => {
    resendLinkMutation.mutate(undefined, {
      onSuccess: () => {
        toast.create({
          title: text(
            'extensions.edit.virtualFax.secureFaxLinkResent',
            'Secure fax link resent',
          ),
          type: 'success',
          duration: TOAST_DURATION_SUCCESS,
          closable: true,
        })
      },
      onError: (error: unknown) => {
        const apiErr = error as ApiError | undefined
        toast.create({
          title: text(
            'extensions.edit.virtualFax.resendFailed',
            'Resend failed',
          ),
          description:
            apiErr?.message ??
            (error as Error)?.message ??
            text(
              'extensions.edit.virtualFax.resendSaveFailed',
              'Failed to resend secure fax link',
            ),
          type: 'error',
          duration: TOAST_DURATION_ERROR,
          closable: true,
        })
      },
    })
  }

  const isPending =
    createExtensionMutation.isPending ||
    updateExtensionMutation.isPending ||
    updateUserGroupsMutation.isPending
  const breadcrumbs = [
    { label: listLabel, path: listPath },
    {
      label: isAddMode
        ? text('extensions.edit.breadcrumbAdd', 'Add {{typeLabel}}', {
            typeLabel,
          })
        : text(
            'extensions.edit.breadcrumbEdit',
            'Edit {{typeLabel}} {{value}}',
            { typeLabel, value: extension?.number ?? extension?.id ?? '' },
          ),
    },
  ]

  if (!isAddMode && (isLoading || !extension)) {
    return renderWithWrapper(
      wrapper,
      breadcrumbs,
      <Box display="flex" justifyContent="center" py={12}>
        <Spinner size="xl" />
      </Box>,
    )
  }
  if (
    !isAddMode &&
    (isError || !extension || extension.type !== ExtensionType.VIRTUAL_FAX)
  ) {
    return renderWithWrapper(
      wrapper,
      breadcrumbs,
      <Card.Root>
        <Card.Body p={6}>
          <Heading size="md" mb={2}>
            {text('extensions.edit.virtualFax.notType', 'Not a virtual fax')}
          </Heading>
          <Link
            to={listPath}
            style={{ color: 'var(--chakra-colors-blue-500)' }}
          >
            {text('extensions.edit.backToList', '← Back to {{listLabel}}', {
              listLabel,
            })}
          </Link>
        </Card.Body>
      </Card.Root>,
    )
  }

  return renderWithWrapper(
    wrapper,
    breadcrumbs,
    <form onSubmit={handleSubmit}>
      <VStack gap={6} align="stretch" css={SWITCH_FORM_CSS}>
        <Card.Root>
          <Card.Header p={6} pb={0}>
            <Card.Title>{typeLabel}</Card.Title>
          </Card.Header>
          <Card.Body p={6} pt={3}>
            <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap={4}>
              <Field.Root required invalid={attemptedSubmit && !name.trim()}>
                <Field.Label>
                  {text('extensions.columns.name', 'Name')}
                </Field.Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} />
              </Field.Root>
              <Field.Root required invalid={attemptedSubmit && !number.trim()}>
                <Field.Label>
                  {text('extensions.columns.number', 'Number')}
                </Field.Label>
                <Input
                  value={number}
                  onChange={(e) => setNumber(e.target.value)}
                />
              </Field.Root>
              <Field.Root gridColumn="1 / -1">
                <Toggle
                  colorPalette="brand"
                  checked={omitFromDirectory}
                  onCheckedChange={(e) =>
                    setOmitFromDirectory(e.checked === true)
                  }
                >
                  {text(
                    'extensions.edit.omitFromDirectory',
                    'Omit from directory',
                  )}
                </Toggle>
              </Field.Root>
            </Grid>
          </Card.Body>
        </Card.Root>

        <Card.Root>
          <Card.Header p={6} pb={0}>
            <Card.Title>
              {text(
                'extensions.edit.virtualFax.receivingOptions',
                'Receiving Options',
              )}
            </Card.Title>
          </Card.Header>
          <Card.Body p={6} pt={3}>
            <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap={4}>
              <Field.Root gridColumn="1 / -1" invalid={emailInvalid}>
                <Field.Label>
                  {text(
                    'extensions.edit.virtualFax.receivingEmailDestination',
                    'Receiving email destination',
                  )}
                </Field.Label>
                <Input
                  value={receivingEmailDestination}
                  onChange={(e) => setReceivingEmailDestination(e.target.value)}
                />
              </Field.Root>
              {!isAddMode && (
                <>
                  <Field.Root gridColumn="1 / -1">
                    <Toggle
                      colorPalette="brand"
                      checked={allowMessageReview}
                      onCheckedChange={(e) =>
                        setAllowMessageReview(e.checked === true)
                      }
                    >
                      {text(
                        'extensions.edit.virtualFax.secureFax',
                        'Secure Fax',
                      )}
                    </Toggle>
                  </Field.Root>
                  {allowMessageReview && (
                    <>
                      <Field.Root>
                        <Field.Label>
                          {text(
                            'extensions.edit.virtualFax.download',
                            'Download',
                          )}
                        </Field.Label>
                        <NativeSelect.Root>
                          <NativeSelect.Field
                            value={secureFaxDownloadCount}
                            onChange={(e) =>
                              setSecureFaxDownloadCount(e.target.value)
                            }
                          >
                            <option value="1">
                              {text(
                                'extensions.edit.virtualFax.singleDownload',
                                'Single Download',
                              )}
                            </option>
                            <option value="5">
                              {text(
                                'extensions.edit.virtualFax.download5',
                                '5 downloads',
                              )}
                            </option>
                            <option value="10">
                              {text(
                                'extensions.edit.virtualFax.download10',
                                '10 downloads',
                              )}
                            </option>
                            <option value="15">
                              {text(
                                'extensions.edit.virtualFax.download15',
                                '15 downloads',
                              )}
                            </option>
                          </NativeSelect.Field>
                          <NativeSelect.Indicator />
                        </NativeSelect.Root>
                      </Field.Root>
                      <Field.Root>
                        <Field.Label>
                          {text(
                            'extensions.edit.virtualFax.failedLoginRetries',
                            'Failed Login Retries',
                          )}
                        </Field.Label>
                        <NativeSelect.Root>
                          <NativeSelect.Field
                            value={secureFaxFailedLoginRetries}
                            onChange={(e) =>
                              setSecureFaxFailedLoginRetries(e.target.value)
                            }
                          >
                            <option value="1">
                              {text('extensions.common.none', 'None')}
                            </option>
                            <option value="5">
                              {text(
                                'extensions.edit.virtualFax.retries5',
                                '5 retries',
                              )}
                            </option>
                            <option value="10">
                              {text(
                                'extensions.edit.virtualFax.retries10',
                                '10 retries',
                              )}
                            </option>
                            <option value="15">
                              {text(
                                'extensions.edit.virtualFax.retries15',
                                '15 retries',
                              )}
                            </option>
                          </NativeSelect.Field>
                          <NativeSelect.Indicator />
                        </NativeSelect.Root>
                      </Field.Root>
                      <Field.Root>
                        <Field.Label>
                          {text(
                            'extensions.edit.virtualFax.linkExpiresIn',
                            'Download Link Expires In',
                          )}
                        </Field.Label>
                        <NativeSelect.Root>
                          <NativeSelect.Field
                            value={secureFaxLinkExpiryMinutes}
                            onChange={(e) =>
                              setSecureFaxLinkExpiryMinutes(e.target.value)
                            }
                          >
                            <option value="1440">
                              {text('extensions.edit.virtualFax.day1', '1 day')}
                            </option>
                            <option value="2880">
                              {text(
                                'extensions.edit.virtualFax.day2',
                                '2 days',
                              )}
                            </option>
                            <option value="4320">
                              {text(
                                'extensions.edit.virtualFax.day3',
                                '3 days',
                              )}
                            </option>
                            <option value="10080">
                              {text(
                                'extensions.edit.virtualFax.day7',
                                '7 days',
                              )}
                            </option>
                            <option value="21600">
                              {text(
                                'extensions.edit.virtualFax.day15',
                                '15 days',
                              )}
                            </option>
                            <option value="43200">
                              {text(
                                'extensions.edit.virtualFax.day30',
                                '30 days',
                              )}
                            </option>
                          </NativeSelect.Field>
                          <NativeSelect.Indicator />
                        </NativeSelect.Root>
                      </Field.Root>
                      <Field.Root gridColumn="1 / -1">
                        <Field.Label>
                          {text(
                            'extensions.edit.virtualFax.changePasswordOptional',
                            'Change secure fax password (optional)',
                          )}
                        </Field.Label>
                        <Input
                          type="password"
                          value={secureFaxPassword}
                          onChange={(e) => setSecureFaxPassword(e.target.value)}
                        />
                        <Input
                          mt={2}
                          type="password"
                          value={secureFaxPasswordConfirmation}
                          onChange={(e) =>
                            setSecureFaxPasswordConfirmation(e.target.value)
                          }
                        />
                      </Field.Root>
                    </>
                  )}
                </>
              )}
            </Grid>
          </Card.Body>
        </Card.Root>

        <Card.Root>
          <Card.Header p={6} pb={0}>
            <Card.Title>
              {text('extensions.edit.userGroups', 'User Groups')}
            </Card.Title>
          </Card.Header>
          <Card.Body p={6} pt={3}>
            <Text fontSize="sm" color="fg.muted" mb={3}>
              {text(
                'extensions.edit.virtualFax.userGroupsDescription',
                'Choose which user groups should have access to this Virtual Fax.',
              )}
            </Text>
            <UserGroupPickList
              allGroups={allUserGroups}
              assignedIds={userGroupIds}
              onAssignedChange={setUserGroupIds}
              assignedGroupNames={Object.fromEntries(
                extVf?.user_groups?.map((group) => [group.id, group.name]) ??
                  [],
              )}
              disabled={isPending}
            />
          </Card.Body>
        </Card.Root>

        {shouldShowResendButton && (
          <Card.Root>
            <Card.Header p={6} pb={0}>
              <Card.Title>
                {text('extensions.common.actions', 'Actions')}
              </Card.Title>
            </Card.Header>
            <Card.Body p={6} pt={3}>
              <Button
                type="button"
                variant="outline"
                onClick={handleResendLink}
                disabled={isPending || resendLinkMutation.isPending}
              >
                {resendLinkMutation.isPending
                  ? text('extensions.edit.virtualFax.sending', 'Sending…')
                  : text(
                      'extensions.edit.virtualFax.resendSecureFaxLink',
                      'Resend secure fax link',
                    )}
              </Button>
            </Card.Body>
          </Card.Root>
        )}

        <Box display="flex" gap={3} pt={2} px={4} pb={3}>
          <Button
            type="submit"
            colorPalette="brand"
            color="brand.contrast"
            disabled={isPending}
            loading={isPending}
            borderRadius="4px"
          >
            {isAddMode
              ? text(
                  'extensions.edit.virtualFax.createButton',
                  'Create Virtual Fax Machine',
                )
              : text(
                  'extensions.edit.virtualFax.updateButton',
                  'Update Virtual Fax Machine',
                )}
          </Button>
          <Button type="button" variant="outline" borderRadius="4px" asChild>
            <Link to={listPath}>
              {text('extensions.common.cancel', 'Cancel')}
            </Link>
          </Button>
        </Box>
      </VStack>
    </form>,
  )
}
